// Nucleo PURO condiviso dalle propagazioni verso le righe degli ordini clienti:
//  • feature 120 (issue #3368) — Anagrafica → Ordini, in `anagrafica-propagazione.ts`
//  • feature 121 (issue #3372) — Articolo   → Ordini, in `articolo-propagazione.ts`
//
// Qui vive SOLO ciò che è identico fra i due assi: la regola di uguaglianza dei valori e i tipi del
// diff. Tutto il resto — mappe dei campi, etichette, sezioni, avvisi, regole di conteggio — è
// specifico di un asse e resta nel proprio file (research D20).
//
// ⚠️ Perché estrarre il nucleo invece di duplicarlo: la regola `null ≡ '' ≡ spazi` è la stessa
// decisione presa due volte; duplicandola, una correzione futura ne aggiusterebbe una sola e i due
// dialoghi comincerebbero a proporre modifiche diverse sugli stessi dati.
//
// ⚠️ Perché NON estrarre di più: i due dialoghi divergono per costruzione (campi utente e sedi
// eliminate di là, sezione unità di misura e predicato ristretto della variante di qua). Una
// generalizzazione più ampia costerebbe più codice dei due file separati — plan.md → Complexity
// Tracking.

// Descrittore di un campo propagabile: `chiave` è il nome della COLONNA DI DESTINAZIONE su
// OrdineVendita, non il nome del campo sorgente. `gruppo` marca i campi retti da una sola casella
// (indirizzo di là, unità di misura di qua): il parametro di tipo lo tiene stretto per asse, così
// un gruppo dell'uno non può finire nell'altro.
export interface CampoPropagabile<T, G extends string = string> {
  chiave: string;
  etichetta: string;
  gruppo?: G;
  getter: (src: T) => string | null | undefined;
}

// Una modifica mostrata nel dialogo: vecchio → nuovo, con la casella di spunta.
export interface ModificaCampo {
  chiave: string;
  etichetta: string;
  vecchio: string;
  nuovo: string;
  selezionato: boolean;
}

// Regola di uguaglianza dei valori: `null` e stringa vuota sono lo stesso valore, e gli spazi ai
// bordi non contano. Il passaggio da assente a vuoto NON è una modifica; `'Mario' → ''` invece lo è,
// e propaga stringa vuota.
export const norm = (v: string | null | undefined): string => (v ?? '').trim();

// Etichetta di un valore assente o vuoto nel dialogo.
export const VALORE_VUOTO = '(vuoto)';

// Elenco delle modifiche fra due stati, limitato ai campi dichiarati propagabili.
export function modificheDi<T, G extends string>(
  campi: CampoPropagabile<T, G>[], prima: T, dopo: T, selezionatoDiDefault = true,
): ModificaCampo[] {
  return campi
    .filter(c => norm(c.getter(prima)) !== norm(c.getter(dopo)))
    .map(c => ({
      chiave: c.chiave,
      etichetta: c.etichetta,
      vecchio: norm(c.getter(prima)) || VALORE_VUOTO,
      nuovo: norm(c.getter(dopo)) || VALORE_VUOTO,
      selezionato: selezionatoDiDefault,
    }));
}
